/**
 * What the pre-signed URLs card should be saying. Three states, not two.
 *
 * It used to compute `inProgress = requested || urls.length === 0`, which rendered two unrelated
 * situations identically: a generation genuinely under way, and an object nobody has ever asked
 * for links for. Both said "Generating pre-signed URLs — they will appear here once ready", so
 * someone looking at an object with nothing requested was told to wait for something that was
 * never coming (Andrew, 2026-09-18).
 *
 * `requested` now means what its name says — a request raised or with the vault in the last hour
 * (backend/src/lib/presigned-url-request.js). With that honest, the three cases separate:
 *
 *   generating   a request is in flight. Hide the current links: they are about to be replaced,
 *                and a stale link that still looks live is worse than a spinner.
 *   noFiles      the vault has answered, and this version has nothing to sign. Terminal, so the
 *                one thing NOT to say is "press refresh" — it produces the same empty answer.
 *                Switch1's 25 May version has been in this state since 6 August.
 *   none         nothing in flight, nothing stored, nobody has asked. Say what to press.
 *   links        show them, expired ones marked as expired — which is what the user needs in
 *                order to realise they should press refresh.
 *
 *   waitingForEgress  the version is still being copied to egress; its request is parked until
 *                it lands, and spRecoverFinished releases it then — no refresh needed. Ranked
 *                just below generating: links still on file are from an EARLIER copy and are
 *                about to be replaced, so they are not shown as if they were current.
 *
 * noFiles is checked before none for the same reason `requested` was split out of it in the first
 * place: "asked, and there is nothing" and "never asked" look identical and need different words.
 * Since requests are only raised once a version is in egress, noFiles means just what it says.
 */
export type PresignedUrlsState = 'generating' | 'waitingForEgress' | 'noFiles' | 'none' | 'links'

export function presignedUrlsState({ requested, urlCount, noFiles = false, waitingForEgress = false }: {
    requested: boolean
    urlCount: number
    noFiles?: boolean
    waitingForEgress?: boolean
}): PresignedUrlsState {
    if (requested) return 'generating'
    if (waitingForEgress) return 'waitingForEgress'
    if (urlCount > 0) return 'links'
    return noFiles ? 'noFiles' : 'none'
}

/** How often the card re-reads while a version is still on its way. */
export const PRESIGNED_POLL_MS = 10_000

/**
 * Whether the card should keep re-reading: only while a version is generating or still copying to
 * egress. The card used to read once and never again, so a generation that finished a minute later
 * sat behind "Generating pre-signed URLs" until the page was reloaded (Andrew, 2026-09-21).
 *
 * Both waits end by themselves (the request completes, the vault reports no files, a generation
 * that died drops out of the one-hour in-flight window, a copy that lands releases its request),
 * so the polling stops by itself and a card with nothing on its way costs nothing. It is safe to
 * stop on the first read that says "complete": the Region Management Lambda inserts every link
 * (spInsertVersionURL, per file) before it calls spCompletePresignedURLRequest, so the read that
 * sees the request complete sees its links too.
 */
export function presignedPollInterval(sections: ChainVersionUrls[] | undefined): number | false {
    const pending = (sections ?? []).some((s) => {
        const state = presignedUrlsState({
            requested: s.requested,
            urlCount: s.files.length,
            noFiles: s.noFiles,
            waitingForEgress: s.waitingForEgress,
        })
        return state === 'generating' || state === 'waitingForEgress'
    })
    return pending ? PRESIGNED_POLL_MS : false
}

/** Under this many minutes the countdown is shown as a warning. */
export const EXPIRY_WARNING_MINUTES = 10

export type LinksExpiry =
    | { kind: 'none' }
    | { kind: 'expired' }
    | { kind: 'live'; minutesLeft: number }

/**
 * When the links on show stop working: the SOONEST expiry among them, because from that moment at
 * least one of them is dead. Links without a URL or a readable expiry are left out.
 *
 * Minutes round UP. The countdown never reads "0m" for a link that still works, and it turns to
 * Expired at the moment the first link does.
 */
export function linksExpiry(
    files: { preSignedUrl?: string | null; expiryDateTime?: string | null }[],
    now: number,
): LinksExpiry {
    let soonest = Infinity
    for (const f of files) {
        if (!f.preSignedUrl || !f.expiryDateTime) continue
        const at = new Date(f.expiryDateTime).getTime()
        if (!Number.isNaN(at) && at < soonest) soonest = at
    }
    if (soonest === Infinity) return { kind: 'none' }
    const ms = soonest - now
    return ms <= 0 ? { kind: 'expired' } : { kind: 'live', minutesLeft: Math.ceil(ms / 60_000) }
}

/** One version of the recovery chain, as the version-urls endpoint returns it. */
export interface ChainVersionUrls<F = unknown> {
    recoverRequestId: number | null
    /** Boot | Full | Int | Log — or null for a response from before the chain existed. */
    tier: string | null
    objectName: string
    files: F[]
    requested: boolean
    noFiles: boolean
    waitingForEgress: boolean
}

/**
 * The endpoint's answer as a list of chain versions, whatever its vintage. It returns `versions`
 * — every version in the chain, in chain order — and still carries the older single-version
 * fields; an older server sends only those, and older still, a bare array of files. All three
 * come out as sections, so the card never has to know which it was given.
 */
export function chainSections<F = unknown>(payload: unknown): ChainVersionUrls<F>[] {
    if (Array.isArray(payload)) {
        return [{ recoverRequestId: null, tier: null, objectName: '', files: payload as F[], requested: false, noFiles: false, waitingForEgress: false }]
    }
    const p = (payload ?? {}) as Record<string, unknown>
    if (Array.isArray(p.versions)) {
        return (p.versions as Record<string, unknown>[]).map((v) => ({
            recoverRequestId: v.recoverRequestId == null ? null : Number(v.recoverRequestId),
            tier: (v.tier as string | null) ?? null,
            objectName: String(v.objectName ?? ''),
            files: (Array.isArray(v.files) ? v.files : []) as F[],
            requested: !!v.requested,
            noFiles: !!v.noFiles,
            waitingForEgress: !!v.waitingForEgress,
        }))
    }
    return [{
        recoverRequestId: null,
        tier: null,
        objectName: String(p.objectName ?? ''),
        files: (Array.isArray(p.files) ? p.files : []) as F[],
        requested: !!p.requested,
        noFiles: !!p.noFiles,
        waitingForEgress: false,
    }]
}
